import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Play, Star, type LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import PlatformKeyVisual from './PlatformKeyVisual';
import type { HomePlatformTheme } from './homeThemes';

export interface HomeShowcaseItem {
  key: string;
  name: string;
  iconUrl?: string;
  onClick: () => void;
}

export interface HomeShowcaseAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}

interface PlatformShowcaseCardProps {
  theme: HomePlatformTheme;
  primaryAction: {
    label: string;
    onClick: () => void;
  };
  secondaryActions: HomeShowcaseAction[];
  items: HomeShowcaseItem[];
  itemLabel: string;
}

export default function PlatformShowcaseCard({
  theme,
  primaryAction,
  secondaryActions,
  items,
  itemLabel,
}: PlatformShowcaseCardProps) {
  const actionGridClass = secondaryActions.length === 1 ? 'grid-cols-1' : 'grid-cols-3';
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const hasMoreItems = items.length > 5;
  const firstPageItems = items.slice(0, 5);
  const secondaryPageItems = items.slice(5);
  const secondaryPages: HomeShowcaseItem[][] = [];
  for (let pageStart = 0; pageStart < secondaryPageItems.length; pageStart += 9) {
    secondaryPages.push(secondaryPageItems.slice(pageStart, pageStart + 9));
  }
  const lastPage = secondaryPages.length;
  const isExpanded = currentPage > 0;
  const shouldShowScrollHint = hasMoreItems && currentPage < lastPage;

  useEffect(() => {
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, lastPage]);

  const handleItemsScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const pageWidth = Math.max(1, scroller.clientWidth);
    const nextPage = Math.min(lastPage, Math.max(0, Math.round(scroller.scrollLeft / pageWidth)));
    setCurrentPage(nextPage);
  };

  const handleItemsPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const handleItemsPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!hasMoreItems || !pointerStartRef.current) return;

    const deltaX = event.clientX - pointerStartRef.current.x;
    const deltaY = event.clientY - pointerStartRef.current.y;
    if (deltaX < -14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      setCurrentPage((page) => Math.min(lastPage, Math.max(1, page)));
    } else if (deltaX > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      const scroller = scrollerRef.current;
      if (scroller && scroller.scrollLeft < scroller.clientWidth * 0.5) {
        setCurrentPage(0);
      }
    }
  };

  const handleItemsPointerEnd = () => {
    pointerStartRef.current = null;
  };

  return (
    <article className={cn('overflow-hidden rounded-[20px] bg-gradient-to-br p-[1px] shadow-[0_16px_42px_rgba(2,6,23,0.34)]', theme.frame, theme.glow)}>
      <div className={cn('home-premium-surface relative overflow-hidden rounded-[19px] bg-gradient-to-br p-2.5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.26),inset_0_-28px_42px_rgba(0,0,0,0.22)]', theme.panel)}>
        <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(255,255,255,0.2),transparent_32%,rgba(255,255,255,0.08)_66%,transparent),radial-gradient(circle_at_16%_14%,rgba(255,255,255,0.16),transparent_20%)]" />
        <div className="absolute inset-y-0 right-0 w-28 bg-[radial-gradient(circle_at_100%_45%,rgba(255,255,255,0.24),transparent_56%)]" />
        <div className="absolute -bottom-8 left-8 h-20 w-52 rounded-full bg-black/24 blur-2xl" />

        <div className="relative z-[1] grid grid-cols-[108px_minmax(0,1fr)] gap-2.5 min-[420px]:grid-cols-[116px_minmax(0,1fr)]">
          <div className="relative">
            <div className="absolute inset-x-3 bottom-[-7px] h-4 rounded-full bg-black/36 blur-md" />
            <PlatformKeyVisual platform={theme.key} className="relative h-[108px] w-[108px] ring-1 ring-white/28 min-[420px]:h-[116px] min-[420px]:w-[116px]" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-[19px] font-black leading-tight text-amber-50">{theme.name}</h3>
              <span className="shrink-0 rounded-full border border-white/18 bg-black/20 px-2 py-0.5 text-[10px] font-bold text-amber-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] ring-1 ring-white/10">
                {theme.tag}
              </span>
            </div>
            <div className="mt-0.5 truncate text-[21px] font-black leading-tight">{theme.title}</div>
            <p className="mt-0.5 line-clamp-2 text-[13px] font-medium leading-snug text-white/78">{theme.description}</p>

            <button
              type="button"
              onClick={primaryAction.onClick}
              className={cn(
                'home-premium-button mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-white/24 bg-gradient-to-r text-base font-black shadow-[inset_0_1px_0_rgba(255,255,255,0.42),inset_0_-10px_16px_rgba(0,0,0,0.16),0_9px_20px_rgba(0,0,0,0.32)] transition-transform active:scale-[0.99]',
                theme.action
              )}
            >
              {primaryAction.label}
              <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/18 bg-black/72 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_0_10px_rgba(255,255,255,0.16)]">
                <Play size={14} fill="currentColor" />
              </span>
            </button>

            <div className={cn('mt-2 grid gap-2', actionGridClass)}>
              {secondaryActions.slice(0, 3).map(({ label, icon: ActionIcon, onClick }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className={cn(
                    'flex min-h-[42px] items-center justify-center gap-1.5 rounded-xl border border-white/18 bg-[linear-gradient(145deg,rgba(255,255,255,0.16),rgba(0,0,0,0.18)_48%,rgba(0,0,0,0.32))] px-2 text-[12px] font-bold text-white/88 shadow-[inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-8px_14px_rgba(0,0,0,0.18)] transition-transform active:scale-[0.98]',
                    secondaryActions.length !== 1 && 'flex-col gap-0.5 px-1 text-[11px]'
                  )}
                >
                  <ActionIcon size={secondaryActions.length === 1 ? 17 : 16} className="text-amber-100" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {items.length > 0 && (
          <div className="relative z-[1] mt-2">
            <div className="mb-1.5 flex items-center gap-1 text-xs font-bold text-amber-50/90">
              <Star size={13} fill="currentColor" />
              {itemLabel}
            </div>
            <div
              ref={scrollerRef}
              onScroll={handleItemsScroll}
              onPointerDown={handleItemsPointerDown}
              onPointerMove={handleItemsPointerMove}
              onPointerCancel={handleItemsPointerEnd}
              onPointerUp={handleItemsPointerEnd}
              className={cn(
                'no-scrollbar flex snap-x snap-mandatory overflow-x-auto pb-0.5 transition-[max-height] duration-300 ease-out',
                hasMoreItems && 'pr-9',
                isExpanded ? 'max-h-[238px]' : 'max-h-[78px]'
              )}
            >
              <div className="grid min-w-full snap-start grid-cols-5 justify-items-center gap-x-2">
                {firstPageItems.map((item, index) => (
                  <ShowcaseItemButton
                    key={`${item.key}-${index}`}
                    item={item}
                    index={index}
                    theme={theme}
                  />
                ))}
              </div>
              {secondaryPages.map((pageItems, pageIndex) => (
                <div
                  key={`page-${pageIndex + 1}`}
                  className="grid min-w-full snap-start grid-flow-col auto-cols-[56px] grid-rows-[repeat(3,74px)] gap-x-2.5 gap-y-2 pl-2"
                >
                  {pageItems.map((item, index) => (
                    <ShowcaseItemButton
                      key={`${item.key}-${pageIndex * 9 + index + 5}`}
                      item={item}
                      index={pageIndex * 9 + index + 5}
                      theme={theme}
                    />
                  ))}
                </div>
              ))}
            </div>
            {shouldShowScrollHint && (
              <div
                className={cn(
                  'pointer-events-none absolute bottom-0 right-0 flex w-10 items-center justify-center rounded-xl bg-gradient-to-l from-black/54 via-black/24 to-transparent transition-[height] duration-300 ease-out',
                  isExpanded ? 'h-[238px]' : 'h-[78px]'
                )}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/18 bg-white/12 text-white shadow-[0_0_18px_rgba(255,255,255,0.18)]">
                  <ChevronRight size={18} />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function ShowcaseItemButton({
  item,
  index,
  theme,
}: {
  item: HomeShowcaseItem;
  index: number;
  theme: HomePlatformTheme;
}) {
  return (
    <button
      type="button"
      onClick={item.onClick}
      className="w-[56px] transition-transform active:scale-95"
    >
      <div className="home-game-icon-frame h-[56px] w-[56px] ring-1 ring-white/24">
        {item.iconUrl ? (
          <img
            src={item.iconUrl}
            alt={item.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className={cn('flex h-full w-full items-center justify-center bg-gradient-to-br text-base font-black', theme.action)}>
            {theme.key === 'ca28' ? index + 1 : item.name.slice(0, 2)}
          </div>
        )}
      </div>
      <div className="mt-0.5 truncate text-center text-[10px] font-semibold text-white/78">{item.name}</div>
    </button>
  );
}
